import type { CompareWindow, DateWindow, PeriodWindow } from "@/analytics/core/types";
import {
  monthStartsInQuarterWindow,
  monthStartsInYearWindow,
  toIsoDate,
} from "@/analytics/core/period-windows";
import type { MetricDefinition } from "@/grid/core/types";
import type {
  AnalyticsRawData,
  DailyActivityRow,
  RepPeriodRow,
  TeamPeriodRow,
} from "@/analytics/core/types";
import { parseISO, startOfQuarter, subQuarters } from "date-fns";

type DailyField = keyof Pick<
  DailyActivityRow,
  "total_calls" | "connected_calls" | "meetings_held" | "sequences"
>;

type PeriodField = "sales_amount" | "opps_generated_count" | "opps_generated_value";

export interface MetricScopeContext {
  teamIds: number[];
  repIds: number[];
  /** When true, team-period metrics read rep grain (Secondary Rep). */
  useRepPeriodGrain: boolean;
}

function sumDailyField(
  repIds: number[],
  rows: DailyActivityRow[],
  window: DateWindow,
  field: DailyField,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const row of rows) {
    if (!repIds.includes(row.rep_id)) continue;
    if (row.activity_date < window.from || row.activity_date > window.to) continue;
    const value = row[field];
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function findTeamPeriodRow(
  rows: TeamPeriodRow[],
  teamId: number,
  periodType: string,
  periodStart: string,
): TeamPeriodRow | undefined {
  return rows.find(
    (row) =>
      row.team_id === teamId && row.period_type === periodType && row.period_start === periodStart,
  );
}

function findRepPeriodRow(
  rows: RepPeriodRow[],
  repId: number,
  periodType: string,
  periodStart: string,
): RepPeriodRow | undefined {
  return rows.find(
    (row) =>
      row.rep_id === repId && row.period_type === periodType && row.period_start === periodStart,
  );
}

function readTeamPeriodField(row: TeamPeriodRow | undefined, field: PeriodField): number | null {
  if (!row) return null;
  return row[field];
}

function readRepPeriodField(row: RepPeriodRow | undefined, field: PeriodField): number | null {
  if (!row) return null;
  return row[field];
}

function sumTeamPeriodForTeams(
  rows: TeamPeriodRow[],
  teamIds: number[],
  periodType: string,
  periodStart: string,
  field: PeriodField,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const teamId of teamIds) {
    const value = readTeamPeriodField(
      findTeamPeriodRow(rows, teamId, periodType, periodStart),
      field,
    );
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function sumRepPeriodForReps(
  rows: RepPeriodRow[],
  repIds: number[],
  periodType: string,
  periodStart: string,
  field: PeriodField,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const repId of repIds) {
    const value = readRepPeriodField(findRepPeriodRow(rows, repId, periodType, periodStart), field);
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function sumRepSqlsForReps(
  rows: RepPeriodRow[],
  repIds: number[],
  periodType: string,
  periodStart: string,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const repId of repIds) {
    const row = findRepPeriodRow(rows, repId, periodType, periodStart);
    if (row?.sqls == null) continue;
    total += row.sqls;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function sumRepPeriodSqlsFromMonthStart(
  rows: RepPeriodRow[],
  repIds: number[],
  fromDate: string,
  throughDate: string,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const row of rows) {
    if (!repIds.includes(row.rep_id)) continue;
    if (row.period_type !== "week") continue;
    if (row.period_start < fromDate) continue;
    if (row.period_start > throughDate) continue;
    if (row.sqls == null) continue;
    total += row.sqls;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function sumTeamPeriodMonths(
  rows: TeamPeriodRow[],
  teamIds: number[],
  field: PeriodField,
  monthStarts: string[],
): number | null {
  let total = 0;
  let hasValue = false;

  for (const monthStart of monthStarts) {
    for (const teamId of teamIds) {
      const value = readTeamPeriodField(
        findTeamPeriodRow(rows, teamId, "month", monthStart),
        field,
      );
      if (value == null) continue;
      total += value;
      hasValue = true;
    }
  }

  return hasValue ? total : null;
}

function sumRepPeriodMonths(
  rows: RepPeriodRow[],
  repIds: number[],
  field: PeriodField,
  monthStarts: string[],
): number | null {
  let total = 0;
  let hasValue = false;

  for (const monthStart of monthStarts) {
    for (const repId of repIds) {
      const value = readRepPeriodField(findRepPeriodRow(rows, repId, "month", monthStart), field);
      if (value == null) continue;
      total += value;
      hasValue = true;
    }
  }

  return hasValue ? total : null;
}

function monthStartsForSpannedPeriod(period: PeriodWindow, through: string): string[] {
  if (period.grain === "year") return monthStartsInYearWindow(period.periodStart, through);
  return monthStartsInQuarterWindow(period.periodStart, through);
}

function periodTypeForGrain(grain: PeriodWindow["grain"]): "week" | "month" {
  return grain === "week" ? "week" : "month";
}

function periodStartForWindow(period: PeriodWindow): string {
  return period.periodStart;
}

/** Resolve one metric total for a date window at the given scope. */
export function resolveMetricValue(
  metric: MetricDefinition,
  window: DateWindow,
  period: PeriodWindow,
  ctx: MetricScopeContext,
  raw: AnalyticsRawData,
): number | null {
  if (metric.source.kind === "daily-sum") {
    return sumDailyField(ctx.repIds, raw.dailyActivities, window, metric.source.field);
  }

  if (metric.source.kind === "rep-period" || metric.source.kind === "rep-period-month-sum") {
    if (metric.source.field !== "sqls") return null;
    if (
      metric.source.kind === "rep-period-month-sum" ||
      period.grain === "month" ||
      period.grain === "year"
    ) {
      return sumRepPeriodSqlsFromMonthStart(
        raw.repPeriodMetrics,
        ctx.repIds,
        window.from,
        window.to,
      );
    }
    const periodType = periodTypeForGrain(period.grain);
    return sumRepSqlsForReps(
      raw.repPeriodMetrics,
      ctx.repIds,
      periodType,
      periodStartForWindow(period),
    );
  }

  const field = metric.source.field;

  if (period.grain === "quarter" || period.grain === "year") {
    const monthStarts = monthStartsForSpannedPeriod(period, window.to);
    if (ctx.useRepPeriodGrain) {
      return sumRepPeriodMonths(raw.repPeriodMetrics, ctx.repIds, field, monthStarts);
    }
    return sumTeamPeriodMonths(raw.teamPeriodMetrics, ctx.teamIds, field, monthStarts);
  }

  const periodType = periodTypeForGrain(period.grain);
  const periodStart = periodStartForWindow(period);

  if (ctx.useRepPeriodGrain) {
    return sumRepPeriodForReps(raw.repPeriodMetrics, ctx.repIds, periodType, periodStart, field);
  }

  return sumTeamPeriodForTeams(raw.teamPeriodMetrics, ctx.teamIds, periodType, periodStart, field);
}

function priorQuarterStart(quarterStartIso: string): string {
  const anchor = parseISO(quarterStartIso);
  return toIsoDate(startOfQuarter(subQuarters(anchor, 1)));
}

function yearStartFromIso(iso: string): string {
  return `${iso.slice(0, 4)}-01-01`;
}

/** Compare window may span a different period anchor; re-derive period context for compare side. */
export function resolveMetricValueForCompare(
  metric: MetricDefinition,
  compare: CompareWindow,
  primaryPeriod: PeriodWindow,
  ctx: MetricScopeContext,
  raw: AnalyticsRawData,
): number | null {
  const comparePeriod: PeriodWindow = {
    grain: primaryPeriod.grain,
    from: compare.from,
    to: compare.to,
    periodStart:
      primaryPeriod.grain === "quarter"
        ? priorQuarterStart(primaryPeriod.periodStart)
        : primaryPeriod.grain === "year"
          ? yearStartFromIso(compare.from)
          : compare.from,
    isPartial: primaryPeriod.isPartial,
    label: compare.label,
  };

  return resolveMetricValue(metric, compare, comparePeriod, ctx, raw);
}

export function computeDelta(
  primary: number | null,
  compare: number | null,
): { delta: number | null; deltaPct: number | null } {
  if (primary == null || compare == null) return { delta: null, deltaPct: null };
  const delta = primary - compare;
  if (compare === 0) return { delta, deltaPct: primary === 0 ? 0 : null };
  return { delta, deltaPct: (delta / compare) * 100 };
}
